import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, map } from 'rxjs';
import {
  Anexo,
  ChecklistItem,
  HistoricoInteracao,
  Orcamento,
  Workflow,
  WorkflowDetalhe,
  WorkflowEtapa,
  WorkflowEtapaTipo,
} from '../models/workflow.models';
import { buildSeedData, SEED_VERSION } from './seed-data';
import { WORKFLOW_ETAPAS, WORKFLOW_ETAPA_INDEX } from './workflow-utils';

@Injectable({ providedIn: 'root' })
export class WorkflowsService {
  private storageKey = 'wf_workflows';
  private versionKey = 'wf_seed_version';
  private workflowsSubject = new BehaviorSubject<WorkflowDetalhe[]>([]);

  constructor() {
    const stored = this.loadFromStorage();
    if (stored.length) {
      this.workflowsSubject.next(stored);
    } else {
      const seed = buildSeedData();
      this.workflowsSubject.next(seed.workflows);
      this.persist(seed.workflows);
    }
  }

  getAll(): Observable<Workflow[]> {
    return this.workflowsSubject.asObservable().pipe(
      map((items) =>
        items.map((workflow) => ({
          id: workflow.id,
          osNumero: workflow.osNumero,
          placa: workflow.placa,
          marca: workflow.marca,
          modelo: workflow.modelo,
          clienteNome: workflow.clienteNome,
          responsavelAtual: workflow.responsavelAtual,
          etapaAtual: workflow.etapaAtual,
          statusGeral: workflow.statusGeral,
          criadoEm: workflow.criadoEm,
        }))
      )
    );
  }

  getAllDetails(): Observable<WorkflowDetalhe[]> {
    return this.workflowsSubject.asObservable();
  }

  getById(id: number): Observable<WorkflowDetalhe | undefined> {
    return this.workflowsSubject.asObservable().pipe(
      map((items) => items.find((item) => item.id === id))
    );
  }

  addWorkflowFromOrcamento(orcamento: Orcamento): WorkflowDetalhe {
    const workflows = this.workflowsSubject.value;
    const newId = workflows.length ? Math.max(...workflows.map((w) => w.id)) + 1 : 1;
    const etapaAtual = WorkflowEtapaTipo.ABERTURA_OS;

    const etapas: WorkflowEtapa[] = WORKFLOW_ETAPAS.map((etapa) => ({
      tipo: etapa.tipo,
      nome: etapa.nome,
      checklist: etapa.tipo === etapaAtual ? [{ id: 1, texto: 'Documentacao OK', ok: false }] : [],
      observacoes: [],
      anexos: [],
      responsavel: orcamento.consultorResponsavel,
      concluido: false,
    }));

    const novoWorkflow: WorkflowDetalhe = {
      id: newId,
      osNumero: `OS-${new Date().getFullYear()}-${1000 + newId}`,
      placa: orcamento.placa,
      marca: orcamento.marca,
      modelo: orcamento.modelo,
      clienteNome: orcamento.clienteNome,
      responsavelAtual: orcamento.consultorResponsavel,
      etapaAtual,
      statusGeral: 'EM_ANDAMENTO',
      criadoEm: new Date(),
      etapas,
      historico: [
        {
          id: newId * 1000 + 1,
          workflowId: newId,
          dataHora: new Date(),
          tipo: 'SISTEMA',
          descricao: 'Workflow criado a partir do orcamento.',
          usuario: 'Sistema',
        } as HistoricoInteracao,
      ],
      comunicacoes: [],
    };

    const updated = [...workflows, novoWorkflow];
    this.workflowsSubject.next(updated);
    this.persist(updated);
    return novoWorkflow;
  }

  setAll(workflows: WorkflowDetalhe[]): void {
    this.workflowsSubject.next(workflows);
    this.persist(workflows);
  }

  avancarEtapa(workflowId: number): void {
    const workflows = this.workflowsSubject.value.map((workflow) => {
      if (workflow.id !== workflowId) {
        return workflow;
      }

      const currentIndex = WORKFLOW_ETAPA_INDEX.get(workflow.etapaAtual) ?? 0;
      const nextEtapa = WORKFLOW_ETAPAS[Math.min(currentIndex + 1, WORKFLOW_ETAPAS.length - 1)];

      const etapasAtualizadas = workflow.etapas.map((etapa) => {
        if (etapa.tipo === workflow.etapaAtual) {
          return {
            ...etapa,
            fim: new Date(),
            concluido: true,
          };
        }
        if (etapa.tipo === nextEtapa.tipo) {
          return {
            ...etapa,
            inicio: new Date(),
            concluido: false,
            responsavel: workflow.responsavelAtual,
          };
        }
        return etapa;
      });

      const finalStatus = nextEtapa.tipo === WorkflowEtapaTipo.CONTROLE_QUALIDADE ? 'CONCLUIDO' : workflow.statusGeral;

      const historicoItem: HistoricoInteracao = {
        id: workflow.id * 1000 + workflow.historico.length + 1,
        workflowId: workflow.id,
        dataHora: new Date(),
        tipo: 'MUDANCA_ETAPA',
        descricao: `Avanco para ${nextEtapa.nome}.`,
        usuario: workflow.responsavelAtual,
      };

      return {
        ...workflow,
        etapaAtual: nextEtapa.tipo,
        statusGeral: finalStatus,
        etapas: etapasAtualizadas,
        historico: [...workflow.historico, historicoItem],
      };
    });

    this.workflowsSubject.next(workflows);
    this.persist(workflows);
  }

  addChecklistItem(workflowId: number, etapaTipo: WorkflowEtapaTipo, texto: string): void {
    if (!texto.trim()) {
      return;
    }
    this.updateWorkflowEtapa(workflowId, etapaTipo, (etapa) => ({
      ...etapa,
      checklist: [
        ...etapa.checklist,
        { id: Date.now(), texto: texto.trim(), ok: false },
      ],
    }));
  }

  toggleChecklistItem(workflowId: number, etapaTipo: WorkflowEtapaTipo, itemId: number): void {
    this.updateWorkflowEtapa(workflowId, etapaTipo, (etapa) => ({
      ...etapa,
      checklist: etapa.checklist.map((item) =>
        item.id === itemId ? { ...item, ok: !item.ok } : item
      ),
    }));
  }

  addObservacao(workflowId: number, etapaTipo: WorkflowEtapaTipo, texto: string): void {
    if (!texto.trim()) {
      return;
    }
    this.updateWorkflowEtapa(workflowId, etapaTipo, (etapa) => ({
      ...etapa,
      observacoes: [...etapa.observacoes, texto.trim()],
    }));
  }

  addAnexoMock(
    workflowId: number,
    etapaTipo: WorkflowEtapaTipo,
    nomeArquivo: string,
    anexadoPor: string
  ): void {
    const anexo: Anexo = {
      id: Date.now(),
      nomeArquivo,
      etapa: etapaTipo,
      anexadoPor,
      dataHora: new Date(),
    };

    this.updateWorkflowEtapa(workflowId, etapaTipo, (etapa) => ({
      ...etapa,
      anexos: [...etapa.anexos, anexo],
    }));
  }

  private updateWorkflowEtapa(
    workflowId: number,
    etapaTipo: WorkflowEtapaTipo,
    updater: (etapa: WorkflowEtapa) => WorkflowEtapa
  ): void {
    const workflows = this.workflowsSubject.value.map((workflow) => {
      if (workflow.id !== workflowId) {
        return workflow;
      }

      const etapas = workflow.etapas.map((etapa) =>
        etapa.tipo === etapaTipo ? updater(etapa) : etapa
      );

      return { ...workflow, etapas };
    });

    this.workflowsSubject.next(workflows);
    this.persist(workflows);
  }

  private loadFromStorage(): WorkflowDetalhe[] {
    if (typeof localStorage === 'undefined') {
      return [];
    }

    const version = Number(localStorage.getItem(this.versionKey) ?? '0');
    if (version !== SEED_VERSION) {
      return [];
    }

    const raw = localStorage.getItem(this.storageKey);
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw) as WorkflowDetalhe[];
    return parsed.map((workflow) => ({
      ...workflow,
      criadoEm: new Date(workflow.criadoEm),
      ultimoContatoEm:
        workflow.ultimoContatoEm === undefined
          ? undefined
          : workflow.ultimoContatoEm
            ? new Date(workflow.ultimoContatoEm)
            : null,
      proximoContatoEm:
        workflow.proximoContatoEm === undefined
          ? undefined
          : workflow.proximoContatoEm
            ? new Date(workflow.proximoContatoEm)
            : null,
      dataPrevistaEntrega:
        workflow.dataPrevistaEntrega === undefined
          ? undefined
          : workflow.dataPrevistaEntrega
            ? new Date(workflow.dataPrevistaEntrega)
            : null,
      etapas: workflow.etapas.map((etapa) => ({
        ...etapa,
        inicio: etapa.inicio ? new Date(etapa.inicio) : undefined,
        fim: etapa.fim ? new Date(etapa.fim) : undefined,
        anexos: etapa.anexos.map((anexo) => ({
          ...anexo,
          dataHora: new Date(anexo.dataHora),
        })),
      })),
      historico: workflow.historico.map((item) => ({
        ...item,
        dataHora: new Date(item.dataHora),
      })),
      comunicacoes: workflow.comunicacoes.map((item) => ({
        ...item,
        dataHora: new Date(item.dataHora),
      })),
    }));
  }

  private persist(workflows: WorkflowDetalhe[]): void {
    if (typeof localStorage === 'undefined') {
      return;
    }
    localStorage.setItem(this.versionKey, String(SEED_VERSION));
    localStorage.setItem(this.storageKey, JSON.stringify(workflows));
  }
}
